import { useEffect } from "react";

const PAGE_TITLE = "How It Works | Accent Productions";

/**
 * Ported from web/src/app/how-it-works/page.tsx (Next.js App Router) — #139.
 *
 * - next/metadata export -> document.title effect.
 * - Renders inside RootLayout (Header + Footer), like the other public
 *   content pages. Linked from the footer of every customer email
 *   (web/src/lib/email-template.ts -> ${SITE_URL}/how-it-works).
 */
export default function HowItWorksPage(): React.JSX.Element {
  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  return (
    <div className="max-w-prose mx-auto px-4 sm:px-6 py-12">
      <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-8">How It Works</h1>

      <div className="space-y-10 text-gray-600">
        {/* For Clients */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
            For Clients
          </h2>
          <div className="space-y-6">
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                1. Inquiry
              </h3>
              <p>
                Tell us about your event via the inquiry form (or your tech
                rider). We use the details to put together a tailored quote.
              </p>
            </section>
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                2. Quote
              </h3>
              <p>
                You receive a quote PDF by email with line items, total (incl.
                GST), and a link to approve. Quotes are valid for 28 days
                unless stated otherwise.
              </p>
            </section>
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                3. Approve &amp; Deposit
              </h3>
              <p>
                Approving the quote confirms your booking. A deposit invoice is
                issued for the amount shown on your quote (typically 50%,
                sometimes less depending on the event). The deposit secures the
                date and crew. Your event isn&apos;t locked in until it&apos;s
                paid.
              </p>
              <p className="mt-2">
                If a deposit isn&apos;t required for your event, we&apos;ll skip
                this step.
              </p>
            </section>
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                4. Balance Invoice
              </h3>
              <p>
                After your event we issue a balance invoice for the remainder.
                Payment is due within 7 days unless we&apos;ve agreed otherwise.
              </p>
            </section>
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                5. Changes
              </h3>
              <p>
                If anything about your event changes, reply to any email from
                us. We can update the quote, regenerate the invoice, and
                re-send the latest version.
              </p>
            </section>
          </div>
        </div>

        {/* For Contractors */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
            For Contractors
          </h2>
          <div className="space-y-6">
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                1. Job Offer
              </h3>
              <p>
                You&apos;ll get an emailed job offer with the date, location,
                your pay, and a job sheet link with everything you need to
                know. Accept or decline directly from the email.
              </p>
            </section>
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                2. Calendar
              </h3>
              <p>
                Once you accept, you&apos;ll get a confirmation email with an
                &quot;Add to Calendar&quot; link. The calendar event includes a
                link straight back to your job sheet.
              </p>
            </section>
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                3. Updates
              </h3>
              <p>
                If anything about the job changes (call time, gear, crew, your
                hours, etc.) we&apos;ll email you an updated job sheet with a
                list of exactly what changed since the last version.
              </p>
            </section>
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                4. After the Event
              </h3>
              <p>
                You&apos;ll be paid by bank transfer once we&apos;ve confirmed
                all crew finished the job. We&apos;ll let you know when payment
                goes out.
              </p>
            </section>
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                5. Questions
              </h3>
              <p>
                Reply to any of our emails or get in touch directly:{" "}
                <a
                  href="mailto:hello@accent-productions.co.nz"
                  className="underline cursor-pointer"
                >
                  hello@accent-productions.co.nz
                </a>
                .
              </p>
            </section>
          </div>
        </div>

        <p className="text-sm text-gray-400 pt-4">Last updated: May 2026</p>
      </div>
    </div>
  );
}
